import * as React from "react";
import { toast } from "react-toastify";
import { FILES_TAGS, filesApi, useGetDirectoryUploadOperationQuery } from "@/api/files";
import { TRANSACTIONS_TAGS } from "@/api/transactions.constraints";
import { useAppDispatch } from "@/store/hooks";

interface Props {
  operationId: string;
  onTerminal: (operationId: string) => void;
}

const isTerminalUploadFailure = (uploadStatus: string | undefined) =>
  uploadStatus === "partially_failed" || uploadStatus === "failed" || uploadStatus === "expired";

export const DirectoryUploadOperationTracker = ({ operationId, onTerminal }: Props) => {
  const dispatch = useAppDispatch();
  const [pollingInterval, setPollingInterval] = React.useState(2000);
  const transactionDiscoveredRef = React.useRef(false);
  const failureNotifiedRef = React.useRef(false);
  const { uploadStatus, familyProcessingStatus, familyGroupingTxId } = useGetDirectoryUploadOperationQuery(
    operationId,
    {
      pollingInterval,
      skipPollingIfUnfocused: true,
      selectFromResult: result => ({
        uploadStatus: result.data?.upload_status,
        familyProcessingStatus: result.data?.family_processing_status,
        familyGroupingTxId: result.data?.family_grouping_tx_id,
      }),
    },
  );
  const uploadFailed = isTerminalUploadFailure(uploadStatus);
  const familyFailed = familyProcessingStatus === "failed";
  const familyComplete = familyProcessingStatus === "complete";
  const ingestionCompleteWithoutFamilyProcessing =
    uploadStatus === "ingestion_completed" && familyProcessingStatus === "not_requested";
  const isTerminal = uploadFailed || familyFailed || familyComplete || ingestionCompleteWithoutFamilyProcessing;

  React.useEffect(() => {
    if (!familyGroupingTxId || transactionDiscoveredRef.current) return;
    transactionDiscoveredRef.current = true;
    dispatch(
      filesApi.util.invalidateTags([
        FILES_TAGS.FILES_SEARCH_TAG,
        FILES_TAGS.FILES_TAG,
        TRANSACTIONS_TAGS.TRANSACTIONS_TAG,
      ]),
    );
  }, [dispatch, familyGroupingTxId]);

  React.useEffect(() => {
    if (!isTerminal) return;
    setPollingInterval(0);
    if (!failureNotifiedRef.current && (uploadFailed || familyFailed)) {
      failureNotifiedRef.current = true;
      toast.error(
        uploadFailed
          ? "Directory upload could not be completed. No family processing was started."
          : "Contract family processing could not be completed. Your uploaded files are still available.",
      );
    }
    onTerminal(operationId);
  }, [familyFailed, isTerminal, onTerminal, operationId, uploadFailed]);

  return null;
};
